import React, { useEffect, useState, useRef } from "react";
import GridViewIcon from "@mui/icons-material/GridView";
import ReorderIcon from "@mui/icons-material/Reorder";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import StarIcon from "@mui/icons-material/Star";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import {
  getEvents,
  selectEvents,
  resetEvents,
  updateEventStatus,
  deleteEvent,
  annotateEvents,
  removeAnnotations,
} from "./eventsSlice";
import { selectFilters, setFilterApplied } from "../filters/filterSlice";
import { selectOrganization } from "../organization/organizationSlice";
import { Filters } from "../filters/Filters";

export function EventsTableWWF() {
  const [state, setState] = useState({ page: 0, rowsPerPage: 10 });
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [listView, setListView] = useState(true);
  const [reload, setReload] = useState(false);
  const [tab, setTab] = useState(0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAnnotateMenu, setShowAnnotateMenu] = useState(false);
  const [selectedAnnotations, setSelectedAnnotations] = useState([]);
  const { species: allSpecies } = useSelector(selectOrganization);
  const prevTab = useRef(tab);
  const isFirstRender = useRef(true);
  const justRan = useRef(false);
  const { results: events, count } = useSelector(selectEvents);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const initialLoadDone = useRef(false);

  const status = (tab) => {
    switch (tab) {
      case 0:
        return "";
      case 1:
        return "FEATURED";
      default:
        return "NONE";
    }
  };

  const { page, rowsPerPage } = state;

  const isAllCurrentPageSelected = () => {
    return (
      events.length > 0 &&
      events.every((event) => selected.includes(event.uuid))
    );
  };

  const handleSelectAll = () => {
    const allSelected = isAllCurrentPageSelected();

    if (allSelected) {
      setSelected(selected.filter((id) => !events.some((e) => e.uuid === id)));
    } else {
      setSelected([
        ...selected,
        ...events.map((e) => e.uuid).filter((id) => !selected.includes(id)),
      ]);
    }
  };

  const handleSelectEvent = (uuid) => {
    if (selected.includes(uuid)) {
      setSelected(selected.filter((id) => id !== uuid));
    } else {
      setSelected([...selected, uuid]);
    }
  };

  // Initial load - fetch events on mount
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      dispatch(getEvents(1, filters.filterApplied, status(tab), rowsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle rowsPerPage changes
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setSelected([]);
    dispatch(resetEvents());
    setState((prev) => ({ ...prev, page: 0 }));
    dispatch(getEvents(1, filters.filterApplied, status(tab), rowsPerPage));
  }, [rowsPerPage, dispatch, tab, filters.filterApplied]);

  // Handle filter changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (justRan.current) {
      justRan.current = false;
      return;
    }
    dispatch(
      getEvents(
        state.page + 1,
        filters.filterApplied,
        status(tab),
        rowsPerPage,
      ),
    );
    justRan.current = true;
    let check = filters.filterApplied ? false : filters.filterApplied;
    dispatch(setFilterApplied(check));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, rowsPerPage, state.page, tab]);

  useEffect(() => {
    if (prevTab !== tab && reload === true) {
      prevTab.current = tab;
    } else {
      setReload(true);
    }
  }, [tab, reload]);

  // Clear selection when select mode is turned off
  useEffect(() => {
    if (selectMode === false) {
      setSelected([]);
    }
  }, [selectMode]);

  // Handle tab changes - fetch new data
  useEffect(() => {
    if (initialLoadDone.current) {
      dispatch(resetEvents());
      setState({ page: 0, rowsPerPage: rowsPerPage });
      dispatch(getEvents(1, filters.filterApplied, status(tab), rowsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const showChanges = async (tabChange = false) => {
    if (!tabChange) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    dispatch(getEvents(1, true, status(tab), rowsPerPage));
  };

  const handleChangePage = (event, newPage) => {
    dispatch(getEvents(newPage + 1, true, status(tab), rowsPerPage));
    setState({ page: newPage, rowsPerPage: state.rowsPerPage });
    setSelected([]);
  };

  const handleChangeRowsPerPage = (event) => {
    setState({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleArchive = () => {
    if (tab !== 1) {
      dispatch(updateEventStatus(selected, "archive"));
    } else {
      dispatch(updateEventStatus(selected, "restore"));
    }
    setSelected([]);
    if (tab === 1) {
      showChanges();
    }
  };

  const handleStar = () => {
    if (tab !== 2) {
      dispatch(updateEventStatus(selected, "feature"));
    } else {
      dispatch(updateEventStatus(selected, "restore"));
    }
    setSelected([]);
    if (tab === 2 || tab === 1) {
      showChanges();
    }
  };

  const handleDelete = () => {
    dispatch(deleteEvent(selected));
    setShowDeleteConfirmation(false);
    setSelected([]);
    showChanges();
  };

  const handleAnnotate = () => {
    dispatch(annotateEvents(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const handleUnAnnotate = () => {
    dispatch(removeAnnotations(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const changeUrl = (img) => img;
  const changeName = (name) => {
    let nameC;
    if (name.includes("PTZ")) {
      nameC = name.split("-");
      name = nameC[1];
    }
    return name;
  };

  // --- FIXED DATE HELPER FUNCTIONS ---

  const parseDateString = (dateString) => {
    if (!dateString) return null;
    if (dateString.includes("T") && dateString.endsWith("Z")) {
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? null : d;
    }
    const parts = dateString.split(/[- :]/);
    if (parts.length >= 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const hours = parts[3] ? parseInt(parts[3], 10) : 0;
      const minutes = parts[4] ? parseInt(parts[4], 10) : 0;
      const seconds = parts[5] ? parseInt(parts[5], 10) : 0;
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        const date = new Date(year, month, day, hours, minutes, seconds);
        return isNaN(date.getTime()) ? null : date;
      }
    }
    return null;
  };

  const formatDateTime = (dateString) => {
    const date = parseDateString(dateString);
    if (!date) return dateString || "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const formatDisplayDate = (dateString) => {
    const date = parseDateString(dateString);
    if (!date) return dateString || "N/A";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // --- END FIXED DATE HELPER FUNCTIONS ---

  const gradientStyle = {
    background:
      "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
    color: "white",
  };

  return (
    <Paper sx={{ p: 0, m: 0, overflow: "hidden", borderRadius: 2 }}>
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
      >
        <DialogTitle>Delete Event?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected event(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirmation(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAnnotateMenu}
        onClose={() => setShowAnnotateMenu(false)}
      >
        <DialogTitle>Annotate Event(s)</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Select all the labels you want to apply (or remove)!
          </DialogContentText>
          <FormGroup>
            {allSpecies.map((species) => (
              <FormControlLabel
                control={<Checkbox name={species.name} />}
                label={species.name}
                key={species.name}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAnnotations([
                      ...selectedAnnotations,
                      species.name,
                    ]);
                  } else {
                    setSelectedAnnotations(
                      selectedAnnotations.filter(
                        (item) => item !== species.name,
                      ),
                    );
                  }
                }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAnnotateMenu(false);
              setSelectedAnnotations([]);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUnAnnotate}>UnAnnotate</Button>
          <Button onClick={handleAnnotate} autoFocus>
            Annotate
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          background: gradientStyle.background,
          zIndex: 2,
        }}
      >
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="All" />
          <Tab label="Featured" />
        </Tabs>

        <Box
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
          }}
        >
          <Filters />
          <Button
            onClick={() => setSelectMode(!selectMode)}
            variant="text"
            size="small"
            sx={{
              color: "white",
              minWidth: isMobile ? "auto" : "auto",
              px: isMobile ? 1 : 2,
              py: isMobile ? 0.5 : 0.5,
              borderRadius: "30px",
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            Select
          </Button>
          {listView ? (
            <Tooltip title="Grid View">
              <GridViewIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="List view">
              <ReorderIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          )}
        </Box>

        {selected.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: isMobile ? 1 : 2,
              py: isMobile ? 1 : 1,
              bgcolor: "rgba(255,255,255,0.95)",
              borderTop: "1px solid #e0e0e0",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selected.length} selected
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: isMobile ? 1 : 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                onClick={() => {
                  setShowAnnotateMenu(true);
                  setSelectedAnnotations([]);
                }}
                sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
              >
                Annotations Menu
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                <div onClick={handleArchive} style={{ cursor: "pointer" }}>
                  {tab === 1 ? (
                    <UnarchiveIcon
                      sx={{ color: "red", fontSize: isMobile ? 20 : 24 }}
                    />
                  ) : (
                    <ArchiveIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  )}
                </div>
                <StarIcon
                  onClick={handleStar}
                  sx={{
                    color: tab === 2 ? "red" : "black",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <DeleteIcon
                  onClick={() => setShowDeleteConfirmation(true)}
                  sx={{
                    color: "red",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: isMobile ? (listView ? 0 : 1) : listView ? 2 : 2,
          minHeight: 400,
        }}
      >
        {listView ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid #e0e0e0",
              overflowX: "auto",
              borderRadius: 0,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectMode && (
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        checked={isAllCurrentPageSelected()}
                        indeterminate={
                          events.some((event) =>
                            selected.includes(event.uuid),
                          ) && !isAllCurrentPageSelected()
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>Event</TableCell>
                  <TableCell>Species</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Camera</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length !== 0 ? (
                  events.map((row) => (
                    <TableRow key={row.uuid}>
                      {selectMode && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(row.uuid)}
                            onChange={() => handleSelectEvent(row.uuid)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href={changeUrl(row.file)}
                        >
                          <img
                            src={changeUrl(row.thumbnail)}
                            height={80}
                            alt=""
                            style={{ borderRadius: 4 }}
                          />
                        </a>
                      </TableCell>
                      <TableCell>
                        {row.species
                          .filter((item) => item.id !== "N/A")
                          .map((item) => (
                            <Chip
                              key={item.id}
                              label={item.name}
                              style={{
                                backgroundColor: item.color,
                                color: "white",
                              }}
                              size="small"
                            />
                          ))}
                      </TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${(row.confidence * 100).toFixed(1)}%`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatDisplayDate(row.date)}</TableCell>
                      <TableCell>{changeName(row.camera_name)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={selectMode ? 7 : 6}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      No events found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box>
            {selectMode && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Checkbox
                  checked={isAllCurrentPageSelected()}
                  indeterminate={
                    events.some((event) => selected.includes(event.uuid)) &&
                    !isAllCurrentPageSelected()
                  }
                  onChange={handleSelectAll}
                />
                <Typography variant="body2" fontWeight={500}>
                  Select All
                </Typography>
              </Box>
            )}
            <Grid container spacing={2} sx={{ justifyContent: "center" }}>
              {events.map((event) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2.4}
                  key={event.uuid}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      maxWidth: 260,
                      borderRadius: 3,
                      boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                      },
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {selectMode && (
                      <Checkbox
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                          zIndex: 1,
                          backgroundColor: "rgba(255,255,255,0.8)",
                          borderRadius: 1,
                        }}
                        checked={selected.includes(event.uuid)}
                        onChange={() => handleSelectEvent(event.uuid)}
                      />
                    )}
                    <Box
                      sx={{
                        position: "relative",
                        pt: "75%",
                        overflow: "hidden",
                        bgcolor: "#f5f5f5",
                      }}
                    >
                      <img
                        src={changeUrl(event.thumbnail)}
                        alt=""
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          p: 1,
                          background:
                            "linear-gradient(transparent, rgba(0,0,0,0.6))",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "white", fontWeight: 500 }}
                        >
                          {changeName(event.camera_name)}
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mb: 1,
                        }}
                      >
                        {event.species
                          .filter((item) => item.id !== "N/A")
                          .slice(0, 3)
                          .map((item) => (
                            <Chip
                              key={item.id}
                              label={item.name}
                              size="small"
                              sx={{
                                backgroundColor: item.color,
                                color: "white",
                                fontSize: "0.65rem",
                                height: 20,
                              }}
                            />
                          ))}
                        {event.species.filter((item) => item.id !== "N/A")
                          .length > 3 && (
                          <Chip
                            label={`+${event.species.filter((item) => item.id !== "N/A").length - 3}`}
                            size="small"
                            sx={{ fontSize: "0.65rem", height: 20 }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Confidence:
                        </Typography>
                        <Typography variant="caption" fontWeight={500}>
                          {(event.confidence * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Date:
                        </Typography>
                        <Typography variant="caption">
                          {formatDisplayDate(event.date)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {events.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">No events found</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />
    </Paper>
  );
}
